import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { CalendarDays, MapPin, ArrowRight, ArrowLeft, Search, Loader2, Mic2 } from "lucide-react";
import { catMeta } from "../lib/categories";
import { slugify } from "../lib/slugify";
import { useCityGeo } from "../lib/useCityGeo";

const dayLabel = (iso) => new Date(iso.length <= 10 ? iso + "T00:00" : iso).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
const timeLabel = (iso) => (!iso || iso.length <= 10) ? "" : new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
const monthKey = (iso) => (iso || "").slice(0, 7);
const monthLabel = (k) => k ? new Date(k + "-01T00:00").toLocaleDateString("en-GB", { month: "long", year: "numeric" }) : "";

export default function EventsBrowse() {
  const [events, setEvents] = useState(null);
  const [query, setQuery] = useState("");
  const [month, setMonth] = useState("all");
  const [category, setCategory] = useState("all");

  useEffect(() => {
    document.title = "All events · TicketsPro.app";
    api.get("/events/browse").then(({ data }) => setEvents(data)).catch(() => setEvents([]));
  }, []);

  const months = useMemo(() => events ? [...new Set(events.map((e) => monthKey(e.event_date)).filter(Boolean))].sort() : [], [events]);
  const categories = useMemo(() => events ? [...new Set(events.map((e) => e.category).filter(Boolean))].sort() : [], [events]);
  const cityNames = useMemo(() => events ? [...new Set(events.map((e) => e.city).filter(Boolean))].sort() : [], [events]);
  const { city, setCity, nearName } = useCityGeo(cityNames);
  const [venue, setVenue] = useState("all");
  const [artist, setArtist] = useState("all");
  const artists = useMemo(() => events ? [...new Map(events.flatMap((e) => e.acts || []).map((a) => [a.slug, a])).values()].sort((a, b) => a.name.localeCompare(b.name)) : [], [events]);
  const venueNames = useMemo(() => {
    if (!events) return [];
    const scope = city === "all" ? events : events.filter((e) => (e.city || "") === city);
    return [...new Set(scope.map((e) => e.venue || e.venue_name).filter(Boolean))].sort();
  }, [events, city]);
  useEffect(() => { setVenue("all"); }, [city]);

  const filtered = useMemo(() => {
    if (!events) return [];
    const q = query.trim().toLowerCase();
    return events.filter((e) => {
      if (month !== "all" && monthKey(e.event_date) !== month) return false;
      if (category !== "all" && (e.category || "") !== category) return false;
      if (city !== "all" && (e.city || "") !== city) return false;
      if (venue !== "all" && (e.venue || e.venue_name || "") !== venue) return false;
      if (artist !== "all" && !(e.acts || []).some((a) => a.slug === artist)) return false;
      if (q && !`${e.title} ${e.business_name}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [events, query, month, category, city, venue, artist]);

  const groups = {};
  filtered.forEach((e) => { const k = (e.event_date || "").slice(0, 10) || "tbc"; (groups[k] = groups[k] || []).push(e); });
  const dates = Object.keys(groups).sort();

  return (
    <div className="min-h-screen bg-[#0d1626] overflow-x-hidden" data-testid="events-browse">
      <div className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-white/80 hover:text-white flex items-center gap-1.5 text-sm font-medium"><ArrowLeft size={16} /> Home</Link>
          <span className="text-white font-bold">TicketsPro.app</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">All events</h1>
        <p className="text-[#8a97a8] mb-8">Browse everything that's on across TicketsPro.</p>

        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1 min-w-0">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8a97a8]" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="browse-search"
              placeholder="Search events or organisers…"
              className="w-full bg-white/[0.04] border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white placeholder:text-[#8a97a8] outline-none focus:border-[#2ec9c9] transition-colors" />
          </div>
          <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="browse-month"
            className="w-full sm:w-auto max-w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-white outline-none focus:border-[#2ec9c9] transition-colors">
            <option value="all" className="text-[#16243b]">All months</option>
            {months.map((m) => <option key={m} value={m} className="text-[#16243b]">{monthLabel(m)}</option>)}
          </select>
          {cityNames.length > 0 && (
            <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="browse-city"
              className="w-full sm:w-auto max-w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-white outline-none focus:border-[#2ec9c9] transition-colors">
              <option value="all" className="text-[#16243b]">All cities</option>
              {cityNames.map((c) => <option key={c} value={c} className="text-[#16243b]">{c}</option>)}
            </select>
          )}
          {venueNames.length > 0 && (
            <select value={venue} onChange={(e) => setVenue(e.target.value)} data-testid="browse-venue"
              className="w-full sm:w-auto sm:max-w-[180px] max-w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-white outline-none focus:border-[#2ec9c9] transition-colors">
              <option value="all" className="text-[#16243b]">All venues</option>
              {venueNames.map((v) => <option key={v} value={v} className="text-[#16243b]">{v}</option>)}
            </select>
          )}
        </div>

        {nearName && city === nearName && (
          <p className="text-xs text-[#2ec9c9] mb-4 flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="browse-near-hint">
            <span className="flex items-center gap-1.5"><MapPin size={13} /> Showing events near you in {nearName}.</span>
            <Link to={`/city/${slugify(nearName)}`} className="underline hover:text-white transition-colors">View the {nearName} page</Link>
            <button onClick={() => setCity("all")} className="underline hover:text-white transition-colors">Show all cities</button>
          </p>
        )}

        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10" data-testid="browse-categories">
            <button onClick={() => setCategory("all")}
              className={`text-sm rounded-full px-3.5 py-1.5 border transition-colors ${category === "all" ? "bg-[#2ec9c9] text-[#0d1626] border-[#2ec9c9] font-medium" : "text-[#8a97a8] border-white/10 hover:border-[#2ec9c9]/50"}`}>All</button>
            {categories.map((c) => {
              const cm = catMeta(c); const CIcon = cm.icon; const on = category === c;
              return (
                <button key={c} onClick={() => setCategory(c)} data-testid={`browse-cat-${c}`}
                  className="text-sm rounded-full px-3.5 py-1.5 border transition-colors flex items-center gap-1.5"
                  style={on ? { background: cm.color, color: "#0d1626", borderColor: cm.color } : { color: cm.color, borderColor: cm.color + "40" }}>
                  <CIcon size={13} /> {c}
                </button>
              );
            })}
          </div>
        )}

        {artists.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10 items-center" data-testid="browse-artists">
            <span className="text-xs uppercase tracking-wide text-[#8a97a8] mr-1 flex items-center gap-1.5"><Mic2 size={13} /> Artists</span>
            <button onClick={() => setArtist("all")} data-testid="browse-artist-all"
              className={`text-sm rounded-full px-3.5 py-1.5 border transition-colors ${artist === "all" ? "bg-[#8b5cf6] text-white border-[#8b5cf6] font-medium" : "text-[#8a97a8] border-white/10 hover:border-[#8b5cf6]/60"}`}>All</button>
            {artists.map((a) => {
              const on = artist === a.slug;
              return (
                <button key={a.slug} onClick={() => setArtist(on ? "all" : a.slug)} data-testid={`browse-artist-${a.slug}`}
                  className={`text-sm rounded-full px-3.5 py-1.5 border transition-colors ${on ? "bg-[#8b5cf6] text-white border-[#8b5cf6] font-medium" : "text-[#c4b5fd] border-[#8b5cf6]/40 hover:border-[#8b5cf6]"}`}>
                  {a.name}
                </button>
              );
            })}
          </div>
        )}

        {events === null ? (
          <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={32} /></div>
        ) : dates.length === 0 ? (
          <p className="text-[#8a97a8] py-20 text-center" data-testid="browse-empty">No events match your search.</p>
        ) : (
          <div className="space-y-10">
            {dates.map((date) => (
              <div key={date} data-testid={`browse-day-${date}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center gap-2 text-white font-semibold min-w-0"><CalendarDays size={18} className="text-[#2ec9c9] shrink-0" /><span className="truncate">{date === "tbc" ? "Date to be confirmed" : dayLabel(date)}</span></div>
                  <span className="text-xs text-[#8a97a8] bg-white/5 rounded-full px-2.5 py-1 shrink-0">{groups[date].length} event{groups[date].length !== 1 ? "s" : ""}</span>
                  <div className="flex-1 h-px bg-white/10 min-w-[8px]" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {groups[date].map((e) => {
                    const cm = catMeta(e.category); const CIcon = cm.icon;
                    return (
                    <a key={e.id} href={`/store/${e.business_slug}/events/${e.slug}`} data-testid={`browse-card-${e.id}`}
                      className="group flex gap-4 rounded-2xl bg-white/[0.04] border border-white/10 p-3 min-w-0 w-full hover:bg-white/[0.08] hover:border-[#2ec9c9]/40 transition-colors">
                      <div className="w-20 h-20 rounded-xl bg-white/10 overflow-hidden shrink-0">
                        {e.image_url && <img src={e.image_url} alt={e.title} className="w-full h-full object-cover" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-[#2ec9c9]">{timeLabel(e.event_date)}</p>
                        <h3 className="text-white font-semibold truncate group-hover:text-[#2ec9c9] transition-colors">{e.title}</h3>
                        <p className="text-xs text-[#8a97a8] mt-0.5 truncate">{e.business_name}</p>
                        <div className="flex items-center gap-2 mt-1 min-w-0">
                          {e.category && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-1 shrink-0" style={{ background: cm.color + "26", color: cm.color }}><CIcon size={10} /> {e.category}</span>}
                          {e.venue_name && <span className="text-xs text-[#8a97a8] flex items-center gap-1 truncate min-w-0"><MapPin size={11} className="shrink-0" /> <span className="truncate">{e.venue_name}</span></span>}
                        </div>
                      </div>
                      <div className="self-center text-right shrink-0">
                        {e.sold_out ? (
                          <span className="inline-block text-[11px] font-bold px-2 py-1 rounded-full bg-[#c0392b] text-white" data-testid={`browse-soldout-${e.id}`}>Sold out</span>
                        ) : (
                          <>
                            <p className="text-white font-bold text-sm">£{e.from_price.toFixed(2)}</p>
                            {e.low_stock ? <p className="text-[10px] font-bold text-[#ff6b6b]" data-testid={`browse-lowstock-${e.id}`}>Only {e.tickets_left} left!</p> : e.multi_price ? <p className="text-[10px] text-[#8a97a8]">from</p> : null}
                          </>
                        )}
                        <ArrowRight size={16} className="text-[#8a97a8] group-hover:text-[#2ec9c9] transition-colors ml-auto mt-1" />
                      </div>
                    </a>
                  );})}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
